var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useMemo } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery, useQueryClient } from "react-query";
import { fetchCoEditionRequests } from "../api/coEditionRequestService";
import { fetchProductNumbersByProductId } from "../api/productNumberService";
import { usePermissions } from "./usePermissions";
import { useGetCurrentUser } from "../utils/useCurrentUser";
import { CoEditionRequestStatus } from "../types/Api";
import { CO_EDITION_REQUESTS_QUERY_KEY } from "./useCoEditionRequestsData";
var CO_PUBLISHER_ROLE = "02";
// Requests that ended without a number; they do not block a new request.
var CLOSED_WITHOUT_NUMBER = [
    CoEditionRequestStatus.REJECTED,
    CoEditionRequestStatus.DECLINED_BY_CO_PUBLISHER,
];
export var useCoEditionNumberRequest = function (product) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _j = usePermissions(), isAgencyWorker = _j.isAgencyWorker, isPublisher = _j.isPublisher, canViewCoEditionRequests = _j.canViewCoEditionRequests;
    var currentUser = useGetCurrentUser().data;
    var requestsData = useQuery([CO_EDITION_REQUESTS_QUERY_KEY, "product", product === null || product === void 0 ? void 0 : product.id], function () { return fetchCoEditionRequests(ky, { productId: product.id }); }, 
    // A Distributor holds no co-edition-number-requests permission (RECEK-75533).
    { enabled: !!(product === null || product === void 0 ? void 0 : product.id) && canViewCoEditionRequests }).data;
    var productNumbersData = useQuery(["productNumbers", product === null || product === void 0 ? void 0 : product.id], function () { return fetchProductNumbersByProductId(ky, product.id); }, { enabled: !!(product === null || product === void 0 ? void 0 : product.id) }).data;
    var requests = (requestsData === null || requestsData === void 0 ? void 0 : requestsData.items) || [];
    // Waiting for the agency (assign / reject).
    var pendingRequest = requests.find(function (request) { return request.status === CoEditionRequestStatus.REQUESTED; });
    // Filed on behalf of the co-publisher, waiting for its consent.
    var awaitingRequest = requests.find(function (request) {
        return request.status === CoEditionRequestStatus.AWAITING_CO_PUBLISHER;
    });
    // The most recent request, when the co-publisher refused it (the owner is told why).
    var latestRequest = __spreadArray([], requests, true).sort(function (a, b) { var _a, _b; return ((_a = b.dateCreated) !== null && _a !== void 0 ? _a : "").localeCompare((_b = a.dateCreated) !== null && _b !== void 0 ? _b : ""); })[0];
    var declinedRequest = (latestRequest === null || latestRequest === void 0 ? void 0 : latestRequest.status) === CoEditionRequestStatus.DECLINED_BY_CO_PUBLISHER
        ? latestRequest
        : undefined;
    var hasActiveRequest = requests.some(function (request) { return !CLOSED_WITHOUT_NUMBER.includes(request.status); });
    var coPublisherRow = (_b = (_a = product === null || product === void 0 ? void 0 : product.title) === null || _a === void 0 ? void 0 : _a.titlePublishers) === null || _b === void 0 ? void 0 : _b.find(function (titlePublisher) {
        var _a;
        return ((_a = titlePublisher.role) === null || _a === void 0 ? void 0 : _a.value) === CO_PUBLISHER_ROLE &&
            !!titlePublisher.publisher;
    });
    var hasAssignedNumber = ((_d = (_c = productNumbersData === null || productNumbersData === void 0 ? void 0 : productNumbersData.items) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0;
    // A publisher user of the organization the awaiting request was filed for (may consent /
    // refuse). Decided by the request's co-publisher, which the backend authorises against.
    var userSourceId = (_e = currentUser === null || currentUser === void 0 ? void 0 : currentUser.activeOrganization) === null || _e === void 0 ? void 0 : _e.sourceId;
    var isCurrentUserCoPublisher = isPublisher &&
        !!userSourceId &&
        userSourceId === ((_f = awaitingRequest === null || awaitingRequest === void 0 ? void 0 : awaitingRequest.coPublisher) === null || _f === void 0 ? void 0 : _f.sourceId);
    var isCurrentUserTitleOwner = isPublisher &&
        !!userSourceId &&
        userSourceId === ((_h = (_g = product === null || product === void 0 ? void 0 : product.title) === null || _g === void 0 ? void 0 : _g.owner) === null || _h === void 0 ? void 0 : _h.sourceId);
    var canRequest = useMemo(function () {
        var _a, _b, _c, _d, _e;
        if (!product || !coPublisherRow || !hasAssignedNumber || hasActiveRequest)
            return false;
        if (isAgencyWorker)
            return true;
        if (isPublisher) {
            var userSourceId_1 = (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.activeOrganization) === null || _a === void 0 ? void 0 : _a.sourceId;
            if (!userSourceId_1)
                return false;
            var ownerSourceId = (_c = (_b = product.title) === null || _b === void 0 ? void 0 : _b.owner) === null || _c === void 0 ? void 0 : _c.sourceId;
            var titlePublisherSourceIds = ((_e = (_d = product.title) === null || _d === void 0 ? void 0 : _d.titlePublishers) === null || _e === void 0 ? void 0 : _e.map(function (tp) { var _a; return (_a = tp.publisher) === null || _a === void 0 ? void 0 : _a.sourceId; })) ||
                [];
            return (userSourceId_1 === ownerSourceId ||
                titlePublisherSourceIds.includes(userSourceId_1));
        }
        return false;
    }, [
        product,
        coPublisherRow,
        hasAssignedNumber,
        hasActiveRequest,
        isAgencyWorker,
        isPublisher,
        currentUser,
    ]);
    var invalidate = function () {
        queryClient.invalidateQueries([
            CO_EDITION_REQUESTS_QUERY_KEY,
            "product",
            product === null || product === void 0 ? void 0 : product.id,
        ]);
        queryClient.invalidateQueries(["productNumbers", product === null || product === void 0 ? void 0 : product.id]);
        queryClient.invalidateQueries(["product", product === null || product === void 0 ? void 0 : product.id]);
    };
    return {
        canRequest: canRequest,
        pendingRequest: pendingRequest,
        awaitingRequest: awaitingRequest,
        declinedRequest: declinedRequest,
        isCurrentUserCoPublisher: isCurrentUserCoPublisher,
        isCurrentUserTitleOwner: isCurrentUserTitleOwner,
        requests: requests,
        coPublisherRow: coPublisherRow,
        invalidate: invalidate,
    };
};
